import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon, RotateCcw } from 'lucide-react';
import { Button } from '../ui/index.js';

/**
 * Error boundary.
 *
 * Without one, a single throw in any page unmounts the entire tree and the
 * browser shows a white rectangle — which is both impossible to diagnose and
 * the worst thing that can happen during a demo. This keeps the failure
 * contained, names it, and offers a way out.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; label?: string },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[boundary]', this.props.label ?? '', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto max-w-xl px-6 py-16">
        <div className="rounded-lg border border-danger-line/60 bg-surface p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-danger-tint text-danger"
            >
              <AlertOctagon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-ink">This view failed to render</h1>
              <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
                The rest of the application is unaffected. The error is below and in the browser
                console.
              </p>
              <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-md border border-line bg-surface-sunken p-3 font-mono text-2xs leading-relaxed text-ink">
                {error.name}: {error.message}
                {error.stack ? `\n\n${error.stack.split('\n').slice(1, 6).join('\n')}` : ''}
              </pre>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  leading={<RotateCcw className="h-3.5 w-3.5" />}
                  onClick={() => this.setState({ error: null })}
                >
                  Try again
                </Button>
                <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
                  Reload
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
